import { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { Context } from "../../context/Context";
import { getSubscriptionSummaryAPI } from "../../utils/api/billing";
import { billingPermissions } from "../modules/billing";
import { REASONS } from "../core/policy";
import { SUBSCRIPTION_GATED_ROLES } from "../core/roles";
import { OPERATIONAL_STATES, operationalAccessFromSummary } from "../subscription";
import { useAccess } from "./useAccess";

/*
  Operational access of a store, shared by every page.

  `can(permission)` answers "does this ROLE hold the permission for this store". This hook adds the second, independent question:
  "is the store's subscription active, so operational actions are allowed today?" (access/subscription.js). Pages ask

      const operational = useOperationalAccess(storeId);
      const send = operational.check("menu.send", { restaurantId: storeId });
      // send.allowed  -> enable the action
      // send.locked   -> show it disabled and explain why (<SubscriptionLockNotice reason={send.reason} />)
      // send.visible  -> false: the role never has this action, do not show it

  A `new` client does not hold the operational permissions; the policy reports them as SUBSCRIPTION_REQUIRED, so the same action is
  shown locked instead of hidden. A `store` / `storeL` customer holds them and is locked only while the subscription is not active.
  The answer comes from the server (one cached request per store); while it is unknown nothing is locked for a role that holds
  the permission (the server stays the authority).
*/

const FRESH_MS = 30 * 1000;
const UNKNOWN = Object.freeze({ state: OPERATIONAL_STATES.UNKNOWN, operational: null, reason: null });

const entries = new Map(); // storeId -> { status, access, at, userKey, promise }
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

const currentToken = () => {
  try {
    return localStorage.getItem("auth-token");
  } catch {
    return null;
  }
};

/** Seeds the cache from summaries a page already loaded (the dashboard), so no second request is made. */
export const primeOperationalAccess = (summaries = [], userKey = "") => {
  (Array.isArray(summaries) ? summaries : []).forEach((summary) => {
    if (!summary?.storeId) return;
    entries.set(summary.storeId, { status: "ready", access: operationalAccessFromSummary(summary), at: Date.now(), userKey, promise: null });
  });
  notify();
};

/** Forgets one store (after a payment, a receipt, a plan change ...) or every store. */
export const invalidateOperationalAccess = (storeId) => {
  if (storeId) entries.delete(storeId);
  else entries.clear();
  notify();
};

const load = (storeId, userKey) => {
  const token = currentToken();
  if (!storeId || !token) return null;
  const current = entries.get(storeId);
  if (current?.promise) return current.promise;
  const promise = Promise.resolve()
    .then(() => getSubscriptionSummaryAPI(token, [storeId]))
    .then((response) => {
      const summary = (response?.data?.summaries || []).find((item) => item.storeId === storeId);
      entries.set(storeId, { status: "ready", access: operationalAccessFromSummary(summary), at: Date.now(), userKey, promise: null });
    })
    .catch(() => {
      // Unknown, never "locked": an unreachable answer must not lock a customer's UI (the server enforces regardless).
      entries.set(storeId, { status: "error", access: UNKNOWN, at: Date.now(), userKey, promise: null });
    })
    .finally(notify);
  entries.set(storeId, { ...(current || { status: "loading", access: UNKNOWN }), status: current?.status === "ready" ? "ready" : "loading", at: current?.at || 0, userKey, promise });
  return promise;
};

// `storeIdOrIds`: one store (a page with a selected store) or several (a page that lists every store of the client). With several, the
// answer is "operational" when ANY of them is, and "locked" only when every one is known to be locked: the per-store decision is then
// taken where a store is chosen.
// `passive`: only read the shared answer, never request it. The dashboard hook loads the summaries itself and primes the cache.
export function useOperationalAccess(storeIdOrIds, { passive = false } = {}) {
  const context = useContext(Context);
  const { can, explain, role } = useAccess();
  // The answer comes from the billing summary, which only billing roles may read. Every subscription-gated role holds `billing.view`;
  // the others (branch, staff without billing) are never locked by the subscription, so they never ask (no 403 on every page).
  const canRead = can(billingPermissions.view);
  const [, rerender] = useReducer((count) => count + 1, 0);
  const userKey = String(context?.currentUserInfo?._id || context?.currentUserInfo?.id || "");
  const lastRole = useRef(role);
  const idsKey = (Array.isArray(storeIdOrIds) ? storeIdOrIds : [storeIdOrIds]).filter(Boolean).map(String).join("|");

  useEffect(() => {
    listeners.add(rerender);
    return () => listeners.delete(rerender);
  }, []);

  useEffect(() => {
    const roleChanged = lastRole.current !== role;
    lastRole.current = role;
    if (passive || !canRead) return;
    idsKey.split("|").filter(Boolean).forEach((storeId) => {
      const entry = entries.get(storeId);
      const stale = !entry || entry.userKey !== userKey || roleChanged || Date.now() - entry.at > FRESH_MS;
      if (!stale) return;
      if (entry && (entry.userKey !== userKey || roleChanged)) entries.delete(storeId);
      load(storeId, userKey);
    });
  }, [idsKey, userKey, role, passive, canRead]);

  const known = (canRead ? idsKey : "")
    .split("|")
    .filter(Boolean)
    .map((storeId) => {
      const entry = entries.get(storeId);
      return entry && entry.userKey === userKey ? entry : null;
    });
  const answers = known.filter(Boolean).map((entry) => entry.access);
  let access = UNKNOWN;
  if (answers.some((item) => item.operational === true)) access = answers.find((item) => item.operational === true);
  else if (answers.length && answers.length === known.length && answers.every((item) => item.operational === false)) access = answers[0];
  const status = !idsKey ? "idle" : known.some((entry) => !entry || entry.status === "loading") ? "loading" : "ready";
  // The subscription is active but the account is still `new` (the role changes when the server promotes the owner): the page asks
  // for a fresh session instead of leaving the customer locked.
  const rolePending = access.operational === true && role === "new";

  const check = useCallback(
    (permission, resource, options) => {
      const decision = explain(permission, resource, options);
      if (decision.allowed) {
        const locked = access.operational === false && SUBSCRIPTION_GATED_ROLES.includes(role);
        return { visible: true, allowed: !locked, locked, reason: locked ? access.reason || "no_subscription" : null };
      }
      if (decision.reason === REASONS.SUBSCRIPTION_REQUIRED) {
        return { visible: true, allowed: false, locked: true, reason: access.operational === true ? "role_pending" : access.reason || "no_subscription" };
      }
      return { visible: false, allowed: false, locked: false, reason: null };
    },
    [explain, access, role],
  );

  return useMemo(
    () => ({ status, state: access.state, operational: access.operational, reason: access.reason, rolePending, check }),
    [status, access, rolePending, check],
  );
}

export default useOperationalAccess;
